import { html, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "@/components/alert";
import "@/components/button";
import "@/components/button-group";
import "@/components/featured-icon";
import "@/components/icon";
import "@/components/textarea";
import "@/components/visually-hidden";
import EventController from "@/common/controllers/event";
import { when } from "@/common/directives/when";
import EkoElement from "@/utilities/element";
import type {
  FeedbackSurveyCancelDetail,
  FeedbackSurveyCloseDetail,
  FeedbackSurveyMode,
  FeedbackSurveySkipDetail,
  FeedbackSurveySubmitDetail,
} from "./types";
import { CsatFeedbackController } from "./csat-feedback";
import style from "./feedback-survey.css?inline";
import { NpsFeedbackController } from "./nps-feedback";

export type {
  FeedbackSurveyCancelDetail,
  FeedbackSurveyCloseDetail,
  FeedbackSurveyMode,
  FeedbackSurveySkipDetail,
  FeedbackSurveySubmitDetail,
};

@customElement("eko-feedback-survey")
class FeedbackSurvey extends EkoElement {
  static styles = [...EkoElement.styles, unsafeCSS(style)];

  // Every dismissal before submitting routes here — the close (X) button,
  // Escape, or a mode's secondary button. CSAT treats a dismissal as `skip`;
  // NPS treats it as `cancel`. Dismissing the success state fires neither, only
  // `close`.
  private _onClose = () => {
    const wasSuccess = this._success;
    this.open = false;
    if (!wasSuccess) {
      if (this.mode === "csat") {
        this.dispatchEvent(
          new CustomEvent<FeedbackSurveySkipDetail>("skip", {
            bubbles: true,
            composed: true,
            detail: { feedbackType: "csat" },
          })
        );
      } else {
        this.dispatchEvent(
          new CustomEvent<FeedbackSurveyCancelDetail>("cancel", {
            bubbles: true,
            composed: true,
            detail: { feedbackType: this.mode },
          })
        );
      }
    }
    this.dispatchEvent(
      new CustomEvent<FeedbackSurveyCloseDetail>("close", {
        bubbles: true,
        composed: true,
        detail: { feedbackType: this.mode },
      })
    );
  };

  private _csatFeedback = new CsatFeedbackController(this, {
    onSkip: () => this._onClose(),
    onSubmit: (d) => this._dispatchSubmit(d),
  });

  @state() private _errorMessage: string | undefined = undefined;

  private _events = new EventController(this);

  @state() private _loading = false;

  private _npsFeedback = new NpsFeedbackController(this, {
    onCancel: () => this._onClose(),
    onSubmit: (d) => this._dispatchSubmit(d),
  });

  private _onDocumentKeydown = (e: Event) => {
    if (!this.open || (e as KeyboardEvent).key !== "Escape") return;
    // Escape closes from anywhere on the page (the survey never steals focus),
    // but not while the feedback field has focus — otherwise a stray Escape
    // mid-sentence would silently discard whatever the user has typed.
    if (this.shadowRoot?.activeElement?.localName === "eko-textarea") return;
    this._onClose();
  };

  @state() private _success = false;

  @property({ attribute: "follow-up-negative" }) followUpNegative: string | undefined = undefined;

  @property({ attribute: "follow-up-neutral" }) followUpNeutral: string | undefined = undefined;

  @property({ attribute: "follow-up-positive" }) followUpPositive: string | undefined = undefined;

  @property({ reflect: true }) mode: FeedbackSurveyMode = "csat";

  @property({ reflect: true, type: Boolean }) open = false;

  @property() question = "How did we do?";

  private _dispatchSubmit(detail: FeedbackSurveySubmitDetail) {
    this._loading = true;
    this._errorMessage = undefined;
    this.dispatchEvent(
      new CustomEvent<FeedbackSurveySubmitDetail>("submit", {
        bubbles: true,
        composed: true,
        detail,
      })
    );
  }

  private _renderForm() {
    const params = {
      followUpNegative: this.followUpNegative,
      followUpNeutral: this.followUpNeutral,
      followUpPositive: this.followUpPositive,
      loading: this._loading,
      question: this.question,
    };
    return html`
      <div class="eko-feedback-survey__header">
        <span class="eko-feedback-survey__eyebrow">
          <eko-icon name="message-question-circle" size="sm"></eko-icon>
          Help us improve your experience
        </span>
        <span class="eko-feedback-survey__question">${this.question}</span>
      </div>
      ${when(
        this._errorMessage,
        html`<div class="eko-feedback-survey__error">
          <eko-alert variant="danger">${this._errorMessage}</eko-alert>
        </div>`
      )}
      ${this.mode === "nps" ? this._npsFeedback.render(params) : this._csatFeedback.render(params)}
    `;
  }

  private _renderSuccess() {
    return html`
      <div class="eko-feedback-survey__success" role="status">
        <eko-featured-icon
          variant="square-modern"
          theme="success"
          icon="check-circle"></eko-featured-icon>
        <div class="eko-feedback-survey__success-text">
          <span class="eko-feedback-survey__success-title">Thank you for your feedback!</span>
          <span class="eko-feedback-survey__success-subtitle">
            Your feedback has been received and will be reviewed by our team.
          </span>
        </div>
      </div>
    `;
  }

  connectedCallback() {
    super.connectedCallback();
    this._events.listen(document, "keydown", this._onDocumentKeydown);
  }

  render() {
    return html`
      <div class="eko-feedback-survey" role="region" aria-label=${this.question}>
        <eko-button
          class="eko-feedback-survey__close"
          square
          variant="plain"
          @click=${this._onClose}>
          <eko-icon name="x-close"></eko-icon>
          <eko-visually-hidden>Close</eko-visually-hidden>
        </eko-button>
        ${this._success ? this._renderSuccess() : this._renderForm()}
      </div>
    `;
  }

  showError(message = "Something went wrong. Please try again.") {
    this._loading = false;
    this._errorMessage = message;
  }

  showSuccess() {
    this._loading = false;
    this._success = true;
  }

  updated(changedProperties: Map<PropertyKey, unknown>) {
    if (changedProperties.has("open") && !this.open) {
      this._loading = false;
      this._errorMessage = undefined;
      this._success = false;
      this._csatFeedback.reset();
      this._npsFeedback.reset();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-feedback-survey": FeedbackSurvey;
  }
}

export default FeedbackSurvey;
